import oneStemBloom from '@/assets/brand/onestem-bloom.svg'
import AppSidebar from '@/components/sidebar/AppSidebar'
import SiteHeader from '@/components/SiteHeader'
import SnapdragonRealtimeBridge from '@/components/SnapdragonRealtimeBridge'
import { StripeConnectOrgSessionProvider } from '@/components/stripe-connect/StripeConnectOrgSessionProvider'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { UpvotyHiddenTrigger } from '@/components/upvoty/UpvotyHiddenTrigger'
import { OrganizationContextProvider } from '@/hooks/useOrganizationContext'
import { TimezoneProvider } from '@/hooks/useTimezoneContext'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { BetaFeatureKey, hasBetaFeature } from '@/lib/beta-features'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import {
  logoutStripeConnectForOrganizationChange,
  shouldWaitForStripeConnectOrganizationChange,
} from '@/lib/stripe-connect-session'
import { idSchema } from '@/lib/utils'
import { createFileRoute, notFound, Outlet } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod/v4'

export const Route = createFileRoute('/_authenticated/$orgId')({
  component: OrgIdLayout,
  head: adminRouteTitleHead,
  params: {
    parse: (params) => z.object({ orgId: idSchema('organization') }).parse(params),
  },
  loader: async ({ context, params: { orgId } }) => {
    // We need to make sure we have the organization info!
    // If the param is invalid, then we throw a redirect to the dashboard
    // If the param is good, then we load the organization
    // If the organization is not found, then we throw a 404
    if (!orgId || !(typeof orgId === 'number')) {
      throw notFound()
    }
    try {
      const organization = await context.queryClient.ensureQueryData(
        organizationQueries.show(orgId)
      )

      return { organization }
    } catch (error) {
      if (error.response?.status === 404) {
        throw notFound()
      }
      throw error
    }
  },
})

function OrgIdLayout() {
  const { organization } = Route.useLoaderData() || {}
  const organizationId = organization?.id
  const snapdragonEnabled =
    !organization?.sales_demo && hasBetaFeature(organization, BetaFeatureKey.Snapdragon)
  const [stripeSessionBoundary, setStripeSessionBoundary] = useState(() => ({
    organizationId,
    ready: !shouldWaitForStripeConnectOrganizationChange(organizationId),
  }))

  if (stripeSessionBoundary.organizationId !== organizationId) {
    // Reset during render so a newly keyed provider cannot commit before the external logout
    // boundary starts. The effect below releases only the latest requested organization.
    setStripeSessionBoundary({
      organizationId,
      ready: !shouldWaitForStripeConnectOrganizationChange(organizationId),
    })
  }

  useEffect(() => {
    // The route ID is the committed tenant context. Preloaded/hovered routes never reach this
    // effect, so only a real organization or impersonation switch ends the prior Stripe session.
    let isCurrentOrganization = true
    const logoutPromise = logoutStripeConnectForOrganizationChange(organizationId)
    const markStripeSessionReady = () => {
      if (!isCurrentOrganization) {
        return
      }

      setStripeSessionBoundary((currentBoundary) =>
        currentBoundary.organizationId === organizationId && !currentBoundary.ready
          ? { ...currentBoundary, ready: true }
          : currentBoundary
      )
    }

    if (logoutPromise) {
      void logoutPromise.then(markStripeSessionReady, markStripeSessionReady)
    } else {
      markStripeSessionReady()
    }

    return () => {
      isCurrentOrganization = false
    }
  }, [organizationId])

  const stripeSessionReady =
    stripeSessionBoundary.organizationId === organizationId && stripeSessionBoundary.ready

  return (
    <OrganizationContextProvider>
      <SnapdragonRealtimeBridge
        key={organization?.id ?? 'unknown-org'}
        organizationId={organization?.id}
        enabled={snapdragonEnabled}
      />
      {import.meta.env.VITE_ENV === 'production' && <UpvotyHiddenTrigger />}
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <img
            src={oneStemBloom}
            alt=""
            aria-hidden="true"
            className="pointer-events-none fixed right-0 bottom-0 z-0 aspect-square w-[clamp(56rem,94vw,93.75rem)] translate-x-[53%] translate-y-[50%] select-none opacity-[0.09] max-md:hidden"
          />
          <SiteHeader />
          <main className="relative z-[1] flex-1 overflow-hidden p-2 has-[[data-page-scroll=document]]:overflow-visible sm:px-4">
            {stripeSessionReady ? (
              <StripeConnectOrgSessionProvider
                key={organizationId ?? 'unknown-org'}
                organizationId={organizationId}
              >
                <TimezoneProvider organization={organization}>
                  <Outlet />
                </TimezoneProvider>
              </StripeConnectOrgSessionProvider>
            ) : null}
          </main>
        </SidebarInset>
      </SidebarProvider>
    </OrganizationContextProvider>
  )
}
